/**
 * Created by Claude on 07.02.2026.
 *
 * Engine-agnostic input event dispatcher.
 * Subscribes to HTML5 PointerEvents and dispatches to PointerHandlers via scene graph traversal.
 */

// Local geometry helpers (no cc.* dependency)
const point = function (x, y) {
    return { x, y };
};

const pointSub = function (p1, p2) {
    return { x: p1.x - p2.x, y: p1.y - p2.y };
};

const InputDispatcher = function () {
    cleverapps.EventEmitter.call(this);

    this.activeTouches = new Map();
    this.initialized = false;

    this.keyPressed = new Set();
    this.combos = [];

    cleverapps.gameManager.on("pause", () => this.endActiveTouches());

    window.addEventListener("pagehide", () => {
        this.endActiveTouches();
    });
    document.addEventListener("freeze", () => {
        this.endActiveTouches();
    });
    window.addEventListener("blur", () => {
        this.endActiveTouches();
    });
    window.addEventListener("resize", () => {
        this.endActiveTouches();
    });
};

InputDispatcher.prototype = Object.create(cleverapps.EventEmitter.prototype);
InputDispatcher.prototype.constructor = InputDispatcher;

InputDispatcher.prototype.isPressed = function (key) {
    return this.keyPressed.has(key);
};

InputDispatcher.prototype.onKeyCombo = function (combo, callback, target) {
    const entry = { combo, callback };
    this.combos.push(entry);

    const handler = {
        clear: function () {
            const index = this.combos.indexOf(entry);
            if (index >= 0) {
                this.combos.splice(index, 1);
            }
        }.bind(this)
    };

    addCleaner(target, () => {
        handler.clear();
    });

    return handler;
};

InputDispatcher.prototype.initialize = function (element) {
    if (this.initialized) {
        return;
    }
    this.initialized = true;
    this.element = element;

    // a mobile browser takes a moving touch as a pan or zoom and cancels the pointer, so a drag
    // dies at its first move; the cocos input manager set this before it was removed (6efb252360f)
    element.style.touchAction = "none";

    element.addEventListener("pointerdown", this.onPointerDown.bind(this), { passive: false });
    element.addEventListener("pointermove", this.onPointerMove.bind(this), { passive: false });
    element.addEventListener("pointerup", this.onPointerUp.bind(this), { passive: false });
    element.addEventListener("pointercancel", this.onPointerCancel.bind(this), { passive: false });
    if (!connector.platform.oneOf(connector.MSIX, connector.MICROSOFT, connector.G5WINDOWS)) {
        element.addEventListener("lostpointercapture", this.onPointerLostCapture.bind(this), false);
    }

    element.addEventListener("wheel", this.onMouseWheel.bind(this), { passive: false });

    document.addEventListener("keydown", this.onKeyDown.bind(this), false);
    document.addEventListener("keyup", this.onKeyUp.bind(this), true);
    window.addEventListener("blur", this.clearPressed.bind(this));
};

InputDispatcher.prototype.getElementRect = function () {
    const rect = this.element.getBoundingClientRect();
    return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
    };
};

InputDispatcher.DELTA_MODE_LINE = 1;
InputDispatcher.DELTA_MODE_PAGE = 2;

InputDispatcher.prototype.getViewportConfig = function () {
    // Use cleverapps.resolution - no cc.view dependency
    const resolution = cleverapps.resolution;
    const dpr = resolution.devicePixelRatio;
    const scale = resolution ? resolution.scale : dpr;
    const isRotated = resolution ? resolution.isRotated : false;
    const viewport = resolution ? resolution.viewportRect : {
        x: 0,
        y: 0,
        width: 0,
        height: 0
    };

    return {
        scaleX: scale / dpr,
        scaleY: scale / dpr,
        viewportX: viewport.x / dpr,
        viewportY: viewport.y / dpr,
        viewportWidth: viewport.width / dpr,
        viewportHeight: viewport.height / dpr,
        isRotated
    };
};

InputDispatcher.prototype.convertPointerEvent = function (event) {
    const rect = this.getElementRect();
    const config = this.getViewportConfig();

    // Convert page coordinates to game coordinates
    const x = event.clientX - rect.left - config.viewportX;
    const y = event.clientY - rect.top - config.viewportY;

    // Apply scale and flip Y axis
    let gameX = x / config.scaleX;
    let gameY = (config.viewportHeight - y) / config.scaleY;

    // Handle rotated mode: CSS rotate(-90deg) swaps screen X/Y axes
    if (config.isRotated) {
        gameX = (config.viewportWidth - y) / config.scaleX;
        gameY = (config.viewportHeight - x) / config.scaleY;
    }

    return {
        pointerId: event.pointerId,
        location: point(gameX, gameY),
        clientX: event.clientX,
        clientY: event.clientY,
        timestamp: event.timeStamp || Date.now(),
        button: event.button
    };
};

InputDispatcher.prototype.createTouch = function (pointerData) {
    const touch = {
        _id: pointerData.pointerId,
        _point: point(pointerData.location.x, pointerData.location.y),
        _startPoint: point(pointerData.location.x, pointerData.location.y),
        _prevPoint: point(pointerData.location.x, pointerData.location.y),
        _button: pointerData.button,
        _lastModified: pointerData.timestamp,

        getID: function () {
            return this._id;
        },
        getLocation: function () {
            return point(this._point.x, this._point.y);
        },
        getLocationX: function () {
            return this._point.x;
        },
        getLocationY: function () {
            return this._point.y;
        },
        getPreviousLocation: function () {
            return point(this._prevPoint.x, this._prevPoint.y);
        },
        getStartLocation: function () {
            return point(this._startPoint.x, this._startPoint.y);
        },
        getDelta: function () {
            return pointSub(this._point, this._prevPoint);
        },
        getLastModified: function () {
            return this._lastModified;
        },
        getButton: function () {
            return this._button;
        },
        stopPropagation: function () {
            this._stopped = true;
        }
    };

    return touch;
};

InputDispatcher.prototype.updateTouch = function (touch, pointerData) {
    touch._prevPoint.x = touch._point.x;
    touch._prevPoint.y = touch._point.y;
    touch._point.x = pointerData.location.x;
    touch._point.y = pointerData.location.y;
    touch._lastModified = pointerData.timestamp;
};

InputDispatcher.prototype.endActiveTouches = function () {
    for (const pointerId of this.activeTouches.keys()) {
        const touch = this.activeTouches.get(pointerId);
        if (touch) {
            this.activeTouches.delete(pointerId);
            this.dispatchTouchCancelled(touch);
        }
    }
};

InputDispatcher.prototype.onPointerDown = function (event) {
    event.preventDefault();
    // preventDefault keeps a text field focused after a tap on the game; the cocos input manager
    // moved the focus to the canvas before it was removed (6efb252360f)
    this.element.focus();

    const prevTouch = this.activeTouches.get(event.pointerId);
    if (prevTouch) {
        this.activeTouches.delete(event.pointerId);
        this.dispatchTouchCancelled(prevTouch);
        return;
    }

    this.element.setPointerCapture(event.pointerId);

    const pointerData = this.convertPointerEvent(event);
    const touch = this.createTouch(pointerData);

    this.activeTouches.set(event.pointerId, touch);

    // Notify listeners before dispatching (for tooltip dismiss etc.)
    // Use manual loop so any listener can call touch.stopPropagation() to cancel further dispatch
    const pointerDownListeners = this.bindings["pointerdown"];
    if (pointerDownListeners) {
        const callbacks = pointerDownListeners.slice();
        for (let i = 0; i < callbacks.length; i++) {
            callbacks[i](touch);
            if (touch._stopped) {
                break;
            }
        }
    }

    if (!touch._stopped) {
        this.dispatchTouchBegan(touch);
    }
};

InputDispatcher.prototype.onPointerMove = function (event) {
    event.preventDefault();

    const touch = this.activeTouches.get(event.pointerId);
    const pointerData = this.convertPointerEvent(event);

    if (touch) {
        this.updateTouch(touch, pointerData);
        this.dispatchTouchMoved(touch);
    }

    if (window.matchMedia("(pointer: fine)").matches) {
        // Mouse hover (no button pressed) - create temporary touch for hover
        const hoverTouch = this.createTouch(pointerData);
        this.dispatchMouseMove(hoverTouch);
    }
};

InputDispatcher.prototype.onPointerUp = function (event) {
    event.preventDefault();

    const touch = this.activeTouches.get(event.pointerId);
    if (!touch) {
        return;
    }

    const pointerData = this.convertPointerEvent(event);
    this.updateTouch(touch, pointerData);

    this.activeTouches.delete(event.pointerId);

    this.dispatchTouchEnded(touch);
};

InputDispatcher.prototype.onPointerCancel = function (event) {
    const touch = this.activeTouches.get(event.pointerId);
    if (!touch) {
        return;
    }

    const pointerData = this.convertPointerEvent(event);
    this.updateTouch(touch, pointerData);

    this.activeTouches.delete(event.pointerId);

    this.dispatchTouchCancelled(touch);
};

InputDispatcher.prototype.onPointerLostCapture = function (event) {
    const touch = this.activeTouches.get(event.pointerId);
    if (touch) {
        this.activeTouches.delete(event.pointerId);
        this.dispatchTouchCancelled(touch);
    }
};

InputDispatcher.prototype.onMouseWheel = function (event) {
    event.preventDefault();

    const pointerData = this.convertPointerEvent(event);
    const config = this.getViewportConfig();

    // Firefox reports the delta in lines (or pages) rather than pixels, so bring it back
    // to pixels before scaling into game units - otherwise one notch travels a few units
    // there and a hundred everywhere else.
    const LINE_HEIGHT = 40;
    let unit = 1;
    if (event.deltaMode === InputDispatcher.DELTA_MODE_LINE) {
        unit = LINE_HEIGHT;
    } else if (event.deltaMode === InputDispatcher.DELTA_MODE_PAGE) {
        unit = config.viewportHeight;
    }

    // Both deltas are stored as a viewport displacement in game units along the GL axes,
    // the same units a scroll container's position is measured in. GL y grows upwards
    // while deltaY is positive when scrolling down, so only y flips sign; deltaX and GL x
    // both grow to the right. Trackpads are the only common source of a non-zero deltaX.
    const scrollEvent = {
        _point: pointerData.location,
        _scrollY: -event.deltaY * unit / config.scaleY,
        _scrollX: event.deltaX * unit / config.scaleX,

        getLocation: function () {
            return point(this._point.x, this._point.y);
        },
        getScrollY: function () {
            return this._scrollY;
        },
        getScrollX: function () {
            return this._scrollX;
        }
    };

    this.dispatchMouseScroll(scrollEvent);
};

InputDispatcher.prototype.onKeyDown = function (event) {
    this.dispatchKeyPressed(InputDispatcher.GetKeyCode(event), event);
};

InputDispatcher.prototype.onKeyUp = function (event) {
    this.dispatchKeyReleased(InputDispatcher.GetKeyCode(event), event);
};

InputDispatcher.GetKeyCode = function (event) {
    if (event.key === "Meta") {
        return InputDispatcher.KEY.meta;
    }
    return event.keyCode;
};

// === Dispatch methods - traverse scene graph and call handlers ===

InputDispatcher.prototype.dispatchTouchBegan = function (touch) {
    this.iterateHandlers((handler) => {
        const claimed = handler.handleTouchBegan(touch);
        if (claimed && handler.swallowEvents) {
            return true;
        }
    });
};

InputDispatcher.prototype.dispatchTouchMoved = function (touch) {
    this.trigger("pointermove", touch);

    this.iterateHandlers((handler) => {
        handler.handleTouchMoved(touch);
    });
};

InputDispatcher.prototype.dispatchTouchEnded = function (touch) {
    this.iterateHandlers((handler) => {
        handler.handleTouchEnded(touch);
    });

    // Notify listeners about unhandled touch (for tap animation etc.)
    if (!touch.handler && this.onUnhandledTouchListener) {
        this.onUnhandledTouchListener(touch);
    }
};

InputDispatcher.prototype.dispatchTouchCancelled = function (touch) {
    this.iterateHandlers((handler) => {
        handler.handleTouchCancelled(touch);
    });
};

InputDispatcher.prototype.dispatchMouseMove = function (event) {
    this.trigger("mousemove", event);

    this.iterateHandlers((handler) => {
        handler.processMouseMove(event);
    });
};

InputDispatcher.prototype.dispatchMouseScroll = function (event) {
    if (event.getScrollY() === 0 && event.getScrollX() === 0) {
        return;
    }

    // Every event is dispatched, including several within one frame: a trackpad reports
    // faster than the game renders, and dropping the extras loses part of the gesture.
    // Repeated notch clicks are throttled by the scroll handler, which knows the device.
    this.trigger("mousescroll", event);

    this.iterateHandlers((handler) => {
        const claimed = handler.handleMouseScroll(event);
        if (claimed) {
            return true;
        }
    });
};

InputDispatcher.prototype.dispatchKeyPressed = function (keyCode) {
    this.keyPressed.add(keyCode);

    const combos = this.combos.slice().sort((a, b) => b.combo.length - a.combo.length);

    for (let i = 0; i < combos.length; i++) {
        const ok = combos[i].combo.every(function (comboKey) {
            return this.keyPressed.has(comboKey);
        }, this);

        if (ok) {
            combos[i].callback();
            break;
        }
    }

    this.trigger("keypressed", keyCode);
};

InputDispatcher.prototype.dispatchKeyReleased = function (keyCode) {
    if (!this.keyPressed.has(keyCode)) {
        return;
    }

    this.keyPressed.delete(keyCode);

    this.trigger("keyreleased", keyCode);
};

InputDispatcher.prototype.clearPressed = function () {
    this.keyPressed.forEach(function (keyCode) {
        this.trigger("keyreleased", keyCode);
    }, this);
    this.keyPressed.clear();
};

function getNodePath(node, nodePathMap) {
    if (nodePathMap.get(node)) {
        return nodePathMap.get(node);
    }

    const path = [];
    let current = node;
    while (current) {
        path.unshift(current);
        current = current.parent;
    }

    nodePathMap.set(node, path);

    return path;
}

function reportStuckHandler(handler, node) {
    if (handler._reportedStuck) {
        return;
    }
    handler._reportedStuck = true;

    const scene = cleverapps.Scenes.GetRunningScene();
    const movingNode = scene && scene.movingNode;

    const classifyNode = (current) => {
        if (current.unit && current.additionalViews) {
            return "UnitView";
        }
        if (current.pool && current.cells && current.map2d) {
            return "Map2dLayerView";
        }
        if (current.layersViews && current.scroller) {
            return "Map2dInnerView";
        }
        if (current.cellView === true) {
            return "cellView";
        }
        if (current instanceof cc.Scene) {
            return "Scene";
        }

        const name = current.constructor && current.constructor.name;
        return name && name.length > 4 ? name : "Node";
    };

    const describeNode = (current) => {
        let part = classifyNode(current);

        if (current.unit && current.unit.code !== undefined) {
            part += `{unit:${current.unit.code}_${current.unit.stage}}`;
        }

        const cell = current.cell;
        if (cell && cell.x !== undefined) {
            part += cell.layer !== undefined ? `{cell:${cell.layer}@${cell.x},${cell.y}}` : `{cell:${cell.x},${cell.y}}`;
        }

        let childrenCount = -1;
        let zOrder = 0;
        try {
            childrenCount = current.children ? current.children.length : 0;
            zOrder = current.getLocalZOrder();
        } catch (ignored) {
            childrenCount = -1;
        }
        part += `(z=${zOrder},ch=${childrenCount},${current.isRunning() ? "running" : "exited"})`;

        const tags = [];
        if (current === scene) {
            tags.push("RUNNING_SCENE");
        } else if (current instanceof cc.Scene) {
            tags.push("DETACHED_SCENE");
        }
        if (movingNode && current === movingNode) {
            tags.push("MOVING_NODE");
        }
        if (current.movingNode && current.movingNode !== current) {
            tags.push("HAS_MOVING_NODE");
        }
        if (!current.parent) {
            tags.push("ROOT");
        }
        if (tags.length) {
            part += `<${tags.join(",")}>`;
        }

        return part;
    };

    const chain = [];
    let current = node;
    let depth = 0;

    while (current && depth < 40) {
        chain.push(describeNode(current));
        current = current.parent;
        depth += 1;
    }

    cleverapps.throwAsync(`node not properly removed from scene | leaf: ${chain.join(" < ")} | created: ${handler._creationStack}`);
}

function compareNodeOrder(handlerA, handlerB, nodePathMap) {
    const nodeA = handlerA.target;
    const nodeB = handlerB.target;

    if (nodeA === nodeB) {
        return 0;
    }

    if (!nodeA.isRunning() && !nodeB.isRunning()) {
        return 0;
    }

    if (!nodeA.isRunning()) {
        return 1;
    }

    if (!nodeB.isRunning()) {
        return -1;
    }

    if (nodeA._globalZOrder !== nodeB._globalZOrder) {
        return Number(nodeB._globalZOrder) - Number(nodeA._globalZOrder);
    }

    const pathA = getNodePath(nodeA, nodePathMap);
    const pathB = getNodePath(nodeB, nodePathMap);

    // Find the lowest common ancestor
    let lcaIndex = 0;
    const minLen = Math.min(pathA.length, pathB.length);
    while (lcaIndex < minLen && pathA[lcaIndex] === pathB[lcaIndex]) {
        lcaIndex++;
    }

    // nodes had no common ancestor, some bug in a parent/child mechanism
    if (lcaIndex === 0) {
        const scene = cleverapps.Scenes.GetRunningScene();

        if (pathA[0] !== scene) {
            reportStuckHandler(handlerA, nodeA);
        }
        if (pathB[0] !== scene) {
            reportStuckHandler(handlerB, nodeB);
        }

        if (pathA[0] !== scene && pathB[0] !== scene) {
            return 0;
        }
        if (pathA[0] !== scene) {
            return 1;
        }
        if (pathB[0] !== scene) {
            return -1;
        }
    }

    // Check if one is an ancestor of the other
    if (lcaIndex === pathA.length) {
        // nodeA is ancestor of nodeB, nodeB should come first (deeper)
        return 1;
    }
    if (lcaIndex === pathB.length) {
        // nodeB is ancestor of nodeA, nodeA should come first (deeper)
        return -1;
    }

    // Different branches - compare z-orders at the divergence point
    const childA = pathA[lcaIndex];
    const childB = pathB[lcaIndex];
    const zA = childA.getLocalZOrder();
    const zB = childB.getLocalZOrder();

    if (zA !== zB) {
        return zB - zA; // Higher z-order first (rendered on top)
    }

    // Same z-order - use child index in parent for stable ordering
    const parent = pathA[lcaIndex - 1];
    const indexA = parent.children.indexOf(childA);
    const indexB = parent.children.indexOf(childB);

    return indexB - indexA; // Later child in array is rendered on top
}

InputDispatcher.prototype.markHandlersDirty = function () {
    this._sortedHandlers = undefined;
};

InputDispatcher.prototype.iterateHandlers = function (iterator) {
    const handlers = this.getHandlersSortedByZOrder();

    for (let i = 0; i < handlers.length; i++) {
        const handler = handlers[i];

        // handler might be removed upon iteration, in this case it still in the handlers cache
        if (handler.destructed) {
            continue;
        }

        const claimed = iterator(handler);
        if (claimed) {
            break;
        }
    }
};

InputDispatcher.prototype.getHandlersSortedByZOrder = function () {
    if (this._sortedHandlers) {
        return this._sortedHandlers;
    }

    const nodePathMap = new Map();

    const handlers = Array.from(cleverapps.pointerManager.handlers).reverse();

    // Sort handlers: nodes rendered later (on top) should receive events first
    handlers.sort((a, b) => compareNodeOrder(a, b, nodePathMap));

    this._sortedHandlers = handlers;

    return handlers;
};

InputDispatcher.KEY = {
    back: 6,
    backspace: 8,
    enter: 13,
    shift: 16,
    ctrl: 17,
    alt: 18,
    escape: 27,
    meta: 91,
    space: 32,
    left: 37,
    up: 38,
    right: 39,
    down: 40,
    Delete: 46,
    a: 65,
    b: 66,
    c: 67,
    d: 68,
    e: 69,
    f: 70,
    g: 71,
    h: 72,
    i: 73,
    j: 74,
    k: 75,
    l: 76,
    m: 77,
    n: 78,
    o: 79,
    p: 80,
    q: 81,
    r: 82,
    s: 83,
    t: 84,
    u: 85,
    v: 86,
    w: 87,
    x: 88,
    y: 89,
    z: 90,
    dash: 189
};